import type { MantineTheme } from '@mantine/core';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import type { ClientTerminology } from '@/hooks/useClientTerminology';

export const getNetworkColors = (
  analysisType: GroupAnalysisType,
  theme: MantineTheme
) => {
  switch (analysisType) {
    case GroupAnalysisType.ENCROACHMENT:
    case GroupAnalysisType.PRIORITY:
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      return [
        theme.other.riskColor.minor,
        theme.other.riskColor.low,
        theme.other.riskColor.high,
      ];
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return [
        theme.colors.blueBrand[2],
        theme.colors.redBrand[1],
        theme.colors.redBrand[2],
        theme.colors.redBrand[4],
      ];
    case GroupAnalysisType.CLEAR:
      return [
        theme.colors.grayBrand[3],
        theme.colors.blueBrand[1],
        theme.colors.blueBrand[2],
        theme.colors.blueBrand[3],
      ];
  }
};

export const getSpanNetworkColors = (
  analysisType: GroupAnalysisType,
  theme: MantineTheme
) => {
  switch (analysisType) {
    case GroupAnalysisType.ENCROACHMENT:
      return [
        theme.colors.blueBrand[3],
        theme.colors.grayBrand[5],
        theme.colors.yellowBrand[5],
        theme.colors.orangeBrand[5],
        theme.colors.redBrand[5],
      ];
    case GroupAnalysisType.PRIORITY:
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      return [
        theme.colors.blueBrand[3],
        theme.colors.blueBrand[1],
        theme.colors.yellowBrand[5],
        theme.colors.orangeBrand[5],
        theme.colors.redBrand[5],
      ];
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return [
        theme.colors.blueBrand[1],
        theme.colors.redBrand[1],
        theme.colors.redBrand[2],
        theme.colors.redBrand[4],
      ];
    case GroupAnalysisType.CLEAR:
      return [theme.colors.grayBrand[3], theme.colors.blueBrand[3]];
  }
};

export const getCategoryTitle = (
  analysisType: GroupAnalysisType,
  formatMessage: ClientTerminology['formatMessage'],
  isSpan = false,
  hasGroupRiskSummary = false
): string => {
  switch (analysisType) {
    case GroupAnalysisType.ENCROACHMENT:
      return `${formatMessage({ id: 'list.encroachmentAnalysisView' })} ${isSpan ? 'level' : 'score'}`;
    case GroupAnalysisType.PRIORITY:
      return `Reliability priority`;
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      if (!isSpan && hasGroupRiskSummary) {
        return `Wildfire risk per year`;
      }
      return `Wildfire priority`;
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return `Critical trees`;
    case GroupAnalysisType.CLEAR:
      return `Clear`;
    case GroupAnalysisType.FALL_IN:
      return `${formatMessage({ id: 'list.fallInHazardTreesCountLabel' })}`;
  }
};

export const getSpanCategories = (
  analysisType: GroupAnalysisType,
  criticalTreesThresholds?: number[]
) => {
  switch (analysisType) {
    case GroupAnalysisType.ENCROACHMENT:
      // Based off encScore
      return [0, 1, 2, 3, 4];
    case GroupAnalysisType.PRIORITY:
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      // Based off priorityScore
      // priority score of 0 means unknown priority
      return [0, 2, 5, 10, 20, 40];
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return criticalTreesThresholds;
    case GroupAnalysisType.CLEAR:
      return [0, 100];
    default:
      return [0, 1];
  }
};

export const getSpanLegendValues = (
  analysisType: GroupAnalysisType,
  criticalTreesThresholds: number[]
) => {
  switch (analysisType) {
    case GroupAnalysisType.ENCROACHMENT:
      return [`None`, `Minor`, `Low`, `Med`, `High`];
    case GroupAnalysisType.PRIORITY:
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      return [`P5`, `P4`, `P3`, `P2`, `P1`];
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return criticalTreesThresholds;
    default:
      return [`Tree risk`, `Clear`];
  }
};

export const getCategoryThresholds = (
  selectedAnalysis: GroupAnalysisType,
  encroachmentThresholds: number[],
  priorityThresholds: number[],
  wildfirePriorityThresholds: number[],
  criticalTreesThresholds: number[],
  clearThresholds: number[]
) => {
  switch (selectedAnalysis) {
    case GroupAnalysisType.ENCROACHMENT:
      return encroachmentThresholds;
    case GroupAnalysisType.PRIORITY:
      return priorityThresholds;
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      return wildfirePriorityThresholds;
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      return criticalTreesThresholds;
    default:
      return clearThresholds;
  }
};

export const getCategoryIndex = (
  categoryThresholds: number[],
  total?: number,
  trailing: boolean = false
) => {
  if (total === null || total === undefined) {
    // First threshold is the unknown default
    return 0;
  }

  let idx = 0;

  // Span counting: [0, 1, 2, 3, 4] 1.1: 2
  // Return the greatest index where the total does not exceed the threshold

  // Group counting: [0, 1, 2, 3, 4] 1.1: 1
  // Return the greatest index where the total does exceed the threshold but before the next threshold

  if (trailing) {
    for (let i = categoryThresholds.length - 1; i >= 0; i--) {
      if (categoryThresholds[i] >= total) {
        idx = i; // Find the smallest index where the total is still less than
      }
    }
  } else {
    for (let i = 0; i < categoryThresholds.length; i++) {
      if (categoryThresholds[i] <= total) {
        idx = i; // Find the largest index where the total is still equal or greater
      }
    }
  }

  return idx;
};
